// BottomChrome — lets a page tell the mobile shell "I own the bottom edge".
//
// W35.6 (2026-09-27): create / edit pages dock a `PageBottomActionBar` with
// the primary submit action. The floating PillNav sat at `fixed bottom-4`
// (z-30) directly over that bar (z-20), so the user saw two competing rows of
// controls and the pill visually crowded the submit CTA. The bar had been
// patched to lift itself by +72px to clear the pill, which stacked two bars
// in a ~200px strip at the bottom of an 844px viewport.
//
// Fix: a page that owns the bottom edge hides the PillNav instead, and the
// bar drops to `bottom-0`. Immersion is declared by the page (not inferred
// from "a bar happens to be mounted") because several pages render their bar
// conditionally — `PairInvitePage` swaps its action row between the pre- and
// post-invite states — and tying the flag to mount-presence would make the
// PillNav blink in and out mid-session.
//
// `useHidePillNav()` is a no-op outside a provider so components rendered in
// isolation (unit tests) don't need the wrapper.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";

interface BottomChromeValue {
  /** True when the current page owns the bottom edge; PillNav is suppressed. */
  pillNavHidden: boolean;
  /** Pages call this to claim the bottom edge. Stable identity, idempotent. */
  hidePillNav: () => void;
}

const BottomChromeContext = createContext<BottomChromeValue | null>(null);

export function BottomChromeProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  // W40.1 F6. The claim used to be a bare boolean that only ever went
  // false -> true, so after opening `/wishes/new` once the PillNav was gone
  // for the rest of the SPA session — a route change swaps the *page*, not the
  // provider's state, and nothing ever set it back. A hard refresh was the
  // only recovery.
  //
  // The fix stores *which route* made the claim and compares it with where we
  // are now, so the flag is derived from the current route rather than
  // accumulated. Reading it that way (rather than resetting state in an effect)
  // also sidesteps effect ordering: React runs child effects before parent
  // effects, so a reset effect keyed on `pathname` would fire *after* the new
  // page's own `useHidePillNav()` and wipe its claim.
  const [claimedAt, setClaimedAt] = useState<string | null>(null);
  // A ref, so `hidePillNav` keeps one identity for the provider's whole life —
  // a page's effect must fire once on mount, not on every navigation.
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;
  const hidePillNav = useCallback(() => setClaimedAt(pathnameRef.current), []);
  const pillNavHidden = claimedAt !== null && claimedAt === pathname;
  const value = useMemo<BottomChromeValue>(
    () => ({ pillNavHidden, hidePillNav }),
    [pillNavHidden, hidePillNav],
  );
  return <BottomChromeContext.Provider value={value}>{children}</BottomChromeContext.Provider>;
}

/**
 * Suppress the mobile PillNav for as long as the calling page is mounted.
 *
 * Call it unconditionally in the page body. Runs in an effect (not during
 * render) because `hidePillNav` updates state owned by the provider, and a
 * render-phase update of a parent component is exactly what React warns
 * against. Leaving the route releases the claim on its own — the flag is
 * `claimedAt === pathname`, so the next route either claims it again or the
 * PillNav comes back.
 */
// Allow non-component exports alongside <BottomChromeProvider/> — the
// consumer hooks are intentionally co-located so a page imports the claim
// and the shell reads the flag from one module. Mirrors the
// `AuthProvider.tsx` / `useAuth` precedent.
// eslint-disable-next-line react-refresh/only-export-components
export function useHidePillNav(): void {
  const ctx = useContext(BottomChromeContext);
  const hidePillNav = ctx?.hidePillNav;
  useEffect(() => {
    hidePillNav?.();
  }, [hidePillNav]);
}

/** Read-only access for shell chrome. `false` when no provider is mounted. */
// eslint-disable-next-line react-refresh/only-export-components
export function usePillNavHidden(): boolean {
  return useContext(BottomChromeContext)?.pillNavHidden ?? false;
}
